import { useRouter } from 'next/router';

import { ReactNode, useEffect, useState } from 'react';

import { useAnalytics } from '@utils/analytics';

interface AdBlockDetectorProps {
  onDetected?: (blocked: boolean) => void;
  checkInterval?: number; // 재확인 주기 (ms)
  continuous?: boolean; // 지속적 감지 여부 (기본값: false)
  children?: ReactNode;
}

export function AdBlockDetector({
  onDetected,
  checkInterval = 5000,
  continuous = false, // 기본값은 한 번만 체크
  children,
}: AdBlockDetectorProps) {
  const router = useRouter();
  // const [isBlocked, setIsBlocked] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(0);
  const { firebase } = useAnalytics();

  // const [
  //   isAdBlockedOpen,
  //   { open: openAdBlockedScreen, close: closeAdBlockedScreen },
  // ] = useModal('ad-block-modal');

  // 1. Script 로딩 테스트 (DNS 차단도 감지)
  const checkScriptLoading = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      // 캐시 버스팅을 위한 타임스탬프 추가
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?t=${Date.now()}`;
      script.async = true;

      script.onerror = () => {
        script.remove();
        resolve(true); // 로드 실패 = 차단됨 (DNS 차단 포함)
      };

      script.onload = () => {
        script.remove();
        // adsbygoogle 객체 확인
        resolve(typeof window?.adsbygoogle === 'undefined');
      };

      document.head.appendChild(script);
    });
  };

  // 2. Fetch 요청 테스트 (DNS 차단 감지)
  const checkFetchRequest = async (): Promise<boolean> => {
    const testUrls = [
      'https://googleads.g.doubleclick.net/pagead/id',
      'https://pagead2.googlesyndication.com/pagead/show_ads.js',
    ];

    try {
      const promises = testUrls.map(async (url) => {
        try {
          await fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-cache',
          });
          return 'loaded';
        } catch {
          return 'blocked';
        }
      });

      const results = await Promise.all(promises);

      // 모든 요청이 차단되면 광고 차단으로 판단
      return results.every((r) => r === 'blocked');
    } catch {
      return true;
    }
  };

  // 지수 백오프로 재시도하며 감지
  const detectAdBlock = async () => {
    // 너무 자주 체크하지 않도록 제한
    const now = Date.now();
    if (now - lastCheckTime < 2000) {
      return;
    }
    setLastCheckTime(now);

    try {
      let isAdBlocked = false;
      const maxRetries = 3;

      // 1초 간격으로 재시도
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          // 1초 대기
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        const results = await Promise.all([
          checkScriptLoading(),
          checkFetchRequest(),
        ]);

        // 디버깅을 위한 로그
        console.log(`AdBlock Detection Results (attempt ${attempt + 1}):`, {
          page: router.pathname,
          scriptLoading: results[0],
          fetchRequest: results[1],
        });

        // Script 또는 Fetch 중 하나라도 차단되면 광고차단으로 판단
        isAdBlocked = results[0] || results[1];

        // 광고차단이 감지되지 않으면 재시도 중단
        if (!isAdBlocked) {
          break;
        }
      }

      // setIsBlocked(isAdBlocked);
      onDetected?.(isAdBlocked);

      if (isAdBlocked) {
        firebase.logEvent('ad_block', {});
        // alert('ADBlock이 감지되었습니다.');
        // openAdBlockedScreen();
      }
    } catch (error) {
      console.error('AdBlock detection error:', error);
    }
  };

  useEffect(() => {
    // 초기 감지 및 페이지 변경시 재감지
    detectAdBlock();

    // continuous가 true일 때만 주기적 재확인
    if (continuous) {
      const interval = setInterval(detectAdBlock, checkInterval);
      return () => clearInterval(interval);
    }
  }, [continuous, checkInterval]); // router.pathname 추가로 페이지 변경시 재실행

  // const isProd = isEnv('production');
  // // isChecking이 완료되고 광고 차단이 감지된 경우 차단 화면 표시 (모든 환경)
  // if (!isChecking && isBlocked && !isProd) {
  //   return (
  //     <>
  //       {children}
  //       <AdBlockScreen
  //         onRetry={detectAdBlock}
  //         name={'ad-block-modal'}
  //         open={isAdBlockedOpen}
  //         // eslint-disable-next-line @typescript-eslint/no-empty-function
  //         onClose={closeAdBlockedScreen}
  //       />
  //     </>
  //   );
  // }

  return <>{children}</>;
}

export default AdBlockDetector;
